import { useState, useRef, FormEvent } from 'react';
import { api } from '../api';
import { Task } from '../types';

interface Props {
  onTaskCreated: (task: Task) => void;
}

export function ChatInput({ onTaskCreated }: Props) {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const text = value.trim();
    if (!text) return;

    setLoading(true);
    setError(null);

    try {
      const parsed = await api.chat.parse(text);
      const task = await api.tasks.create({ ...parsed, source: 'manual' });
      onTaskCreated(task);
      setValue('');
      inputRef.current?.focus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2 w-full max-w-2xl">
      <div className="relative flex-1">
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          <span className="text-indigo-400 text-sm font-medium">✦</span>
        </div>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder='Try "follow up with Dana by July 9" or "review Q3 report urgent"'
          className="w-full bg-gray-900 border border-gray-700 text-white placeholder-gray-500 rounded-lg pl-8 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
          disabled={loading}
        />
        {error && (
          <p className="absolute -bottom-5 left-0 text-xs text-red-400">{error}</p>
        )}
      </div>
      <button
        type="submit"
        disabled={loading || !value.trim()}
        className="bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition flex items-center gap-1.5 whitespace-nowrap"
      >
        {loading ? (
          <>
            <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Parsing...
          </>
        ) : (
          <>Add Task</>
        )}
      </button>
    </form>
  );
}
